/**
 * Audio (07): everything the player hears, synthesised - there is not one audio file in the game.
 * The app talks to this interface only. It feeds in sim events and the world each frame; nothing
 * here ever reaches back into the simulation.
 *
 *   const audio = createAudio();
 *   addEventListener('pointerdown', () => audio.resume());     // browsers need a gesture
 *   audio.beginMission(def);
 *   // each frame: audio.handle(frameEvents, world, focusX, focusZ); audio.update(world, dt, focusX, focusZ);
 */

import type { MissionDef, SimEvent, World } from '../sim/types';
import { audioContextClass } from './engine/graph';
import { LiveAudio } from './engine/live';
import { type AudioSettings, DEFAULT_SETTINGS, mergeSettings } from './settings';

export type { AudioSettings } from './settings';

export type UiSound = 'move' | 'confirm' | 'back' | 'stamp' | 'type' | 'medal' | 'promotion' | 'memorial';

export interface GameAudio {
  /** Must be called from a user gesture at least once; safe to call repeatedly. */
  resume(): void;
  settings(): AudioSettings;
  applySettings(next: Partial<AudioSettings>): void;
  /** Menus. */
  playTitleTheme(): void;
  stopMusic(): void;
  uiClick(kind: UiSound): void;
  /** A mission begins: choose biome ambience, reset adaptive layers. */
  beginMission(def: MissionDef): void;
  /** One-shot events from the sim this frame. `listenerX/Z` is the camera focus. */
  handle(events: readonly SimEvent[], world: World, listenerX: number, listenerZ: number): void;
  /**
   * Continuous sounds: footsteps by surface and gait for the ranger AND nearby enemies, the
   * Osprey by phase and distance, the mini-bunker whine, wind, the clock's last-60-seconds
   * urgency, adaptive music layers by `world.alert`. Pass `dt = 0` (or stop calling) while paused.
   */
  update(world: World, dt: number, listenerX: number, listenerZ: number): void;
  endMission(): void;
  dispose(): void;
}

/** What `createAudio` returns where there is no WebAudio: it remembers its settings and does nothing else. */
class SilentAudio implements GameAudio {
  private current: AudioSettings = { ...DEFAULT_SETTINGS };

  resume(): void {}
  settings(): AudioSettings {
    return { ...this.current };
  }
  applySettings(next: Partial<AudioSettings>): void {
    this.current = mergeSettings(this.current, next);
  }
  playTitleTheme(): void {}
  stopMusic(): void {}
  uiClick(): void {}
  beginMission(): void {}
  handle(): void {}
  update(): void {}
  endMission(): void {}
  dispose(): void {}
}

/** Never throws: without an `AudioContext` (node, a locked-down webview) the game simply runs silent. */
export function createAudio(): GameAudio {
  const Ctor = audioContextClass();
  if (!Ctor) return new SilentAudio();
  try {
    return new LiveAudio(Ctor);
  } catch {
    return new SilentAudio();
  }
}
